'use client';

// src/utils/translation.ts

import 'dayjs/locale/ar';
import 'dayjs/locale/ur';
/* eslint-disable consistent-return */
import 'moment/locale/ar';
import 'moment/locale/ur';

import dayjs from 'dayjs';
import moment from 'moment';
import i18next from 'i18next';
import { toast } from 'sonner';
import Cookies from 'js-cookie';
import isToday from 'dayjs/plugin/isToday';
import isTomorrow from 'dayjs/plugin/isTomorrow';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import en from 'src/locales/en.json';
import ar from 'src/locales/ar.json';
import { getLocationByCord } from 'src/api/location/location';
import { ENV } from 'src/global-config';

dayjs.extend(customParseFormat);
dayjs.extend(isToday);
dayjs.extend(isTomorrow);
export const translate = (key) => i18next.t(key);
export const objectToFormData = (obj) => {
  const formData = new FormData();

  Object.entries(obj).forEach(([key, value]) => {
    if (value instanceof File || value instanceof Blob) {
      formData.append(key, value);
    } else if (typeof value === 'object' && value !== null) {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, value);
    }
  });

  return formData;
};

/**
 * @param {'success' | 'error' | 'info' | 'warning' | 'default' | 'loading'} type
 * @param {string} message
 */
export const notify = (type, messsage) => {
  switch (type) {
    case 'success':
      toast.success(messsage || 'Operation Successfull');
      break;
    case 'error':
      toast.error(messsage || 'Something went wrong');
      break;
    case 'info':
      toast.info(messsage || 'Operation Successfull');
      break;
    case 'warning':
      toast.warning(messsage || 'Operation Successfull');
      break;
    case 'default':
      toast.message(messsage || 'Operation Successfull');
      break;
    case 'loading':
      toast.loading(messsage || 'Operation Successfull');
      break;

    default:
      break;
  }
};

export const extractImageListByDriver = (orders = [], driverID) =>
  orders
    .filter((order) => order.driverID === driverID)
    .flatMap((order) => {
      const images = [];

      // Handle pickupPoints
      (order?.pickupPoints ?? []).forEach((point) => {
        const pointKey = Object.keys(point)[0]; // e.g., "pickupPoint"
        const pointData = point?.[pointKey];

        if (pointData?.imagePaths && typeof pointData.imagePaths === 'object') {
          Object.entries(pointData.imagePaths).forEach(([_, path]) => {
            if (path) {
              images.push({
                url: path,
                title: pointKey,
              });
            }
          });
        }
      });

      // Handle deliveryPoints
      (order?.deliveryPoints ?? []).forEach((point) => {
        const pointKey = Object.keys(point)[0]; // e.g., "deliveryPoint1"
        const pointData = point?.[pointKey];

        if (pointData?.imagePaths && typeof pointData.imagePaths === 'object') {
          Object.entries(pointData.imagePaths).forEach(([_, path]) => {
            if (path) {
              images.push({
                url: path,
                title: pointKey,
              });
            }
          });
        }
      });

      return images;
    });

const cardTypeIcons = {
  visa: 'logos:visa',
  mastercard: 'logos:mastercard',
  master: 'logos:mastercard',
  mada: 'simple-icons:mada',
  'stc pay': 'simple-icons:stc',
  stcpay: 'simple-icons:stc',
  amex: 'logos:american-express',
  'american express': 'logos:american-express',
  jcb: 'logos:jcb',
  'diners club': 'logos:diners-club-international',
  diners: 'logos:diners-club-international',
  discover: 'logos:discover',
  maestro: 'logos:maestro',
  paypal: 'logos:paypal',
  default: 'mdi:credit-card-outline',
};

export const visibleViewsInPaymentModal = {
  addCard: 'add-card',
  stc: 'stc',
  verifyStc: 'verify-stc',
};
export const getCardTypeIcon = (cardType) => {
  const type = cardType?.toUpperCase();

  switch (type) {
    case 'VISA':
      return 'payments:visa';
    case 'MASTERCARD':
      return 'payments:mastercard';
    case 'AMERICAN_EXPRESS':
    case 'AMEX':
      return 'logos:amex';
    case 'DINERS':
    case 'DINERS_CLUB':
    case 'DINERS_CLUB_INTER':
      return 'logos:dinersclub';
    case 'DISCOVER':
      return 'logos:discover';
    case 'JCB':
      return 'logos:jcb';
    case 'MADA':
      return 'arcticons:mada-pay';
    default:
      return undefined;
  }
};

export const paymentModes = [
  {
    id: 'credit',
    i18Key: 'credit',
  },
  {
    id: 'pad',
    i18Key: 'Pay_At_Pickup',
  },
  {
    id: 'stc',
    i18Key: 'STC_Pay',
  },
  {
    id: 'add-card',
    i18Key: 'Add_Card',
  },
];

// eslint-disable-next-line arrow-body-style
export const filteredPredictionFromTypes = (response) => {
  return response?.predictions?.find((pred) =>
    pred?.types?.find(
      (e) =>
        e == 'neighborhood' ||
        e == 'locality' ||
        e == 'political' ||
        e == 'administrative_area_level_2' ||
        e == 'administrative_area_level_1'
    )
  );
};

// utils/cookieUtils.ts

export const getCookie = (name) => {
  const cookieStr = decodeURIComponent(document.cookie);
  const cookies = cookieStr.split(';');

  for (let c of cookies) {
    c = c.trim();
    if (c.startsWith(`${name}=`)) {
      return c.substring(name.length + 1);
    }
  }

  return '';
};

export const setCookie = (name, value, exDays) => {
  const d = new Date();
  d.setTime(d.getTime() + exDays * 24 * 60 * 60 * 1000);
  const expires = 'expires=' + d.toUTCString();
  document.cookie = `${name}=${value};${expires};domain=.shahen-sa.com;path=/`;
};

/**
 * Validates if the input is a valid Saudi STC Pay number.
 * Supports both 05xxxxxxxx and 9665xxxxxxxx formats.
 *
 * @param {string} number - The input number.
 * @returns {boolean} - True if valid STC Pay number.
 */
export function isValidSTCPayNumber(number) {
  if (typeof number !== 'string') return false;

  const normalized = number.replace(/\D/g, ''); // remove non-numeric

  // Local format: 05xxxxxxxx (10 digits)
  const localRegex = /^05\d{8}$/;

  // International format: 9665xxxxxxxx (12 digits)
  const intlRegex = /^9665\d{8}$/;

  return localRegex.test(normalized) || intlRegex.test(normalized);
}

export const getUserLocation = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
    }
    navigator.geolocation.getCurrentPosition(
      (position) => resolve(position.coords),
      (error) => reject(error),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  });

// If you have helper functions like getCurrentLang(), keep exporting them too
export function getCurrentLang(langCode = i18next.language) {
  const [language] = langCode.split('-');
  return {
    value: language,
    label: language === 'ar' ? 'Arabic' : 'English',
    adapterLocale: language === 'ar' ? 'ar-sa' : 'en',
  };
}

/**
 * Here this functiion retrun key passed with langauge code
 * like name--> namear, nameurdu, name
 * @param {string} key
 * @returns
 */
export const getLabelForlanguage = (key) => {
  const lang = localStorage.getItem('i18nextLng') || 'ar';

  switch (lang) {
    case 'ar':
      return `${key}ar`;
    case 'ur':
      return `${key}urdu`;
    default:
      return key;
  }
};

/**
 * Here this functiion retrun key passed with langauge code
 * like name--> nameAr, nameurdu, name
 * @param {string} key
 * @returns
 */
export const getLabelForlanguageCapital = (key) => {
  const lang = localStorage.getItem('i18nextLng') || 'ar';
  switch (lang) {
    case 'ar':
      return `${key}Ar`;
    case 'ur':
      return `${key}Urdu`;
    default:
      return key;
  }
};

/**
 * Here this functiion retrun key passed with langauge code
 * like name--> nameara, nameurd, nameen
 * @param {string} key
 * @returns
 */
export const getNameForLanguage = (key) => {
  const lang = localStorage.getItem('i18nextLng') || 'ar';

  switch (lang) {
    case 'ar':
      return `${key}ara`;
    case 'ur':
      return `${key}urd`;
    default:
      return key === 'nameen' ? key : `${key}en`;
  }
};

export const getTruckType = (truckSize = [], selectedTruckSize) => {
  if (!Array.isArray(truckSize)) return [];
  return truckSize.find((item) => item?.id === selectedTruckSize?.id)?.truckDetails || [];
};

export const getLoadType = (truckType, selectedTruckType) => {
  if (!Array.isArray(truckType)) return [];
  return truckType.find((item) => item?.id === selectedTruckType?.id)?.loadTypeArray || [];
};

/**
 * Format a single time slot string using locale
 * @param {string} slot - e.g., "10:24 AM - 11:54 AM"
 * @returns {string} localized time range
 */
export function formatTimeSlot(slot) {
  let locale = localStorage.getItem('i18nextLng') || 'ar';
  if (!slot || typeof slot !== 'string') return '';

  const [startRaw, endRaw] = slot.split(' - ');

  moment.locale(locale);

  const start = moment(startRaw, 'hh:mm A');
  const end = moment(endRaw, 'hh:mm A');

  if (!start.isValid() || !end.isValid()) return slot;

  const useArabicDigits = ['ar', 'ur'].includes(locale);

  const toArabicDigits = (str) => str.replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)]);

  const formatWithMeridiem = (m) => {
    const hour = m.format('hh');
    const minute = m.format('mm');
    const isAM = m.format('A') === 'AM';

    const meridiem = locale === 'ar' || locale === 'ur' ? (isAM ? 'ص' : 'م') : isAM ? 'AM' : 'PM';

    let formatted = `${hour}:${minute} ${meridiem}`;
    return useArabicDigits ? toArabicDigits(formatted) : formatted;
  };

  return `${formatWithMeridiem(start)} - ${formatWithMeridiem(end)}`;
}

/**
 * Format a date string into localized "ddd DD MMM" format
 * @param {string} dateStr - e.g., "14-07-2025"
 * @param {string} locale - 'en', 'ar', 'ur'
 * @returns {string} formatted date like "Tue 15 Jul"
 */
export function formatDateLabel(dateStr) {
  let locale = localStorage.getItem('i18nextLng') || 'ar';

  const date = dayjs(dateStr, 'DD-MM-YYYY');
  dayjs.locale(locale);

  if (!date.isValid()) return dateStr;

  if (date.isToday()) {
    return locale === 'ar' ? 'اليوم' : locale === 'ur' ? 'آج' : 'Today';
  }

  if (date.isTomorrow()) {
    return locale === 'ar' ? 'غدًا' : locale === 'ur' ? 'کل' : 'Tomorrow';
  }

  return date.format('ddd DD MMM');
}

export const getCityDetails = (refObj) => {
  let refKey = [];
  const specialCities = [
    'Sudair Industry And Business City Saudi Arabia',
    'Sudair Industrial And Business City Saudi Arabia',
    'sudair industrial and business city saudi arabia',
    'sudair industry and business city saudi arabia',
    'Sudair Industrial And Business City',
    'Sudair industry and Business City',
    'sudair industry and business city',
    'sudair industrial and business city',
  ];
  refObj.map((res) => {
    if (specialCities.includes(res.long_name) || specialCities.includes(res.short_name)) {
      if (
        res.types.find(
          (e) =>
            e == 'neighborhood' ||
            e == 'locality' ||
            e == 'political' ||
            e == 'administrative_area_level_2' ||
            e == 'administrative_area_level_1'
        )
      ) {
        refKey.push(res.long_name);
        return refKey;
      }
    } else if (
      res.types.find(
        (e) =>
          e == 'locality' ||
          e == 'administrative_area_level_2' ||
          e == 'administrative_area_level_1'
      )
    ) {
      refKey.push(res.long_name);
      return refKey;
    }
  });
  return refKey;
};
/**
 * Extracts the country short name (e.g., 'IN') from a Google Maps address_components array.
 *
 * @param {Array} addressComponents - The address_components array from Google Maps API.
 * @returns {string|null} The country short name, or null if not found.
 */
export const getCountryKey = (addressComponents) => {
  const country = addressComponents.find((comp) => comp.types.includes('country'));
  return country?.short_name || null;
};

export function processGeocodeResponse(data) {
  // console.info('=========================================================');

  let isSudair = false;

  // Check in plus_code
  if (data?.plus_code?.compound_code && data.plus_code.compound_code.includes('Sudair')) {
    console.info('Sudair', data);
    isSudair = true;
  } else {
    console.info('Outside Sudair');
    isSudair = false;
  }

  // Loop through results
  if (Array.isArray(data?.results)) {
    for (const result of data.results) {
      // console.info(result.formatted_address);
      // console.info('=========================================================');
      if (result.formatted_address.includes('Sudair')) {
        isSudair = true;
        break;
      }
    }
  }

  var address = {
    isSudair,
    addresses: data?.results?.map((r) => r.formatted_address) || [],
  };

  // console.info('Found Address : ', address);
  return isSudair;
}

export const getLocationDetails = (placeData, user) => {
  const components = placeData?.address_components || [];

  // Helper to find a component by type
  // const getComponent = (type) =>
  //   components.find((comp) => comp.types.includes(type))?.long_name || '';

  const getComponent = (matchTypes) =>
    components.find((comp) => matchTypes.some((type) => comp.types.includes(type)))?.long_name ||
    '';

  const city = processGeocodeResponse(placeData)
    ? 'Sudair industry and Business City'
    : getComponent(['neighborhood']) ||
    getComponent(['locality']) ||
    getComponent(['political']) ||
    getComponent(['administrative_area_level_2']) ||
    getComponent(['administrative_area_level_1']) ||
    getComponent(['administrative_area_level_3']) ||
    getComponent(['sublocality']);
  // const state = getComponent(['administrative_area_level_1', 'administrative_area_level_2']);
  // const country = getComponent(['country']);

  const lat = placeData?.geometry?.location?.lat || null;
  const long = placeData?.geometry?.location?.lng || null;

  return {
    city,
    cityName: city,
    address: placeData?.formatted_address,
    // address: [city, state, country].filter(Boolean).join(', '),
    lat,
    long,
    personName: user?.userName,
    mobileNumber: user?.mobileNumber,
  };
};
export const getLongLat = (placeData) => {
  const lat = placeData?.geometry?.location?.lat || null;
  const long = placeData?.geometry?.location?.lng || null;

  return [long, lat];
};

/**
 * Processes a legs array from Google Maps directions to:
 * - Get total distance in kilometers (rounded up)
 * - Get an array of durations in minutes
 *
 * @param {Array} legs - Array of route legs with distance and duration objects
 * @returns {{ totalDistanceKm: number, durationsInMinutes: number[] }}
 */
export function getRouteSummary(legs = []) {
  let totalDistanceMeters = 0;
  const durationsInMinutes = [];

  legs.forEach((leg) => {
    if (leg.distance?.value) {
      totalDistanceMeters += leg.distance.value;
    }
    if (leg.duration?.value) {
      durationsInMinutes.push(Math.round(leg.duration.value / 60));
    }
  });

  const totalDistanceKm = Math.ceil(totalDistanceMeters / 1000);

  return {
    totalDistanceKm,
    durationsInMinutes,
  };
}

/**
 * Parses a date and time slot, subtracts 45 minutes from both start and end,
 * and returns formatted parts.
 *
 * @param {{ date: string, slot: string }} input
 * @returns {{ day: string, month: string, year: string, startTime: string, endTime: string }}
 */
export function transformDateSlot(input) {
  const [day, month, year] = input.date.split('-');

  const [startStr, endStr] = input.slot.split(' - ');

  const toDate = (timeStr) => {
    const [time, modifier] = timeStr.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    if (modifier === 'PM' && hours !== 12) hours += 12;
    if (modifier === 'AM' && hours === 12) hours = 0;

    const dateObj = new Date(
      `${year}-${month}-${day}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`
    );
    return dateObj;
  };

  const formatTime = (dateObj) => {
    const hours = dateObj.getHours();
    const minutes = dateObj.getMinutes();
    const suffix = hours >= 12 ? 'PM' : 'AM';
    const hour12 = hours % 12 || 12;
    return `${String(hour12).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${suffix}`;
  };

  const startDate = toDate(startStr);
  const endDate = toDate(endStr);

  // Subtract 45 minutes
  startDate.setMinutes(startDate.getMinutes() - 45);
  endDate.setMinutes(endDate.getMinutes() - 45);

  return {
    day,
    month,
    year,
    startTime: formatTime(startDate),
    endTime: formatTime(endDate),
  };
}

export const formatDate = (date) => dayjs(date).format('YYYY-MM-DD');

export function clearAllCookies() {
  const allCookies = Cookies.get();
  for (const cookieName in allCookies) {
    Cookies.remove(cookieName, {
      path: '/',
      domain: '.shahen-sa.com',
    });
  }
}

export const checkIsCitySudair = (places) => {
  const specialCities = [
    'Sudair Industry And Business City Saudi Arabia',
    'Sudair Industrial And Business City Saudi Arabia',
    'sudair industrial and business city saudi arabia',
    'sudair industry and business city saudi arabia',
    'Sudair Industrial And Business City',
    'Sudair industry and Business City',
    'sudair industry and business city',
    'sudair industrial and business city',
  ];

  const selectedPrediction = places?.predictions?.find((pred) =>
    pred?.address_components?.some(
      (component) =>
        specialCities.includes(component?.long_name) ||
        specialCities.includes(component?.short_name)
    )
  );
  return selectedPrediction;
};

export const getPlusCodeResult = async (place) => {
  const response = await getLocationByCord(`${place?.lat},${place?.lng}`);
  return response;
};

const translations = { en, ar };

// ✅ Client-side: read locale from localStorage (fallback to 'en')
export function getClientLocale() {
  if (typeof window !== 'undefined') {
    try {
      const appSettings = JSON.parse(localStorage.getItem('app-settings') || '{}');
      return appSettings.locale || 'en';
    } catch {
      return 'en';
    }
  }
  return 'en';
}

// ✅ Client-side: read locale from localStorage (fallback to 'en')
export function getUIMode() {
  if (typeof window !== 'undefined') {
    try {
      const appSettings = JSON.parse(localStorage.getItem('app-settings') || '{}');
      return appSettings.mode || 'dark';
    } catch {
      return 'dark';
    }
  }
  return 'dark';
}

// ✅ Server-side: you pass the locale explicitly (from params, headers, cookies)
export function getServerTranslation(locale, key) {
  const dict = translations[locale] || translations.en;
  return dict[key] || key;
}

// ✅ Client-side: auto-pick locale from settings
export function t(key) {
  const locale = getClientLocale();

  const dict = translations[locale] || translations.en;
  console.log('CLIEN LOCALE : ', locale, localStorage.getItem('app-settings'));
  return dict[key] || key;
}

export const _socials = [
  {
    value: 'facebook',
    label: 'Facebook',
    url: 'https://www.facebook.com/SHAHEN-SA-105402241801913',
  },
  { value: 'instagram', label: 'Instagram', url: 'https://www.instagram.com/shahenapp' },
  {
    value: 'linkedin',
    label: 'Linkedin',
    url: 'https://www.linkedin.com/company/shahen-%D8%B4%D8%AD%D9%86/',
  },
  {
    value: 'youtube',
    label: 'Youtube',
    url: 'https://www.youtube.com/channel/UCxRUhbWAfLkQldm3QwA73UQ',
  },
  { value: 'twitter', label: 'Twitter', url: 'https://twitter.com/shahenapp' },
];

export const cardsList = [
  {
    label: 'Mada Card',
    component: 'mada',
  },
  { label: 'Visa Card', logo: 'logos:visa' },
  { label: 'Master Card', logo: 'logos:mastercard' },
  {
    label: 'Apple Pay',
    logo: 'logos:apple-pay',
  },
  {
    label: 'Google Pay',
    logo: 'logos:google-pay',
  },
  {
    label: 'STC Pay',
    component: 'stc',
  },
];

export const countriesBasedOnTypeAndEnvironment = (type) => {
  if (type !== 'pickupLocationDetails') return [];

  return ENV === 'qa' || ENV === 'dev' ? ['IN', 'SA'] : ['SA'];
};
